/**
 * The company registry - master-detail over every acquired company.
 *
 * Phase 4 put 208,000 companies in the database and nothing could read them
 * back. This is the screen that makes them usable.
 *
 * Three structural decisions:
 *
 *   1. **Master-detail, not a wide scrolling table.** The brief is explicit: the
 *      left column must never move. Scanning a list and reading a profile are
 *      different activities, and a table wide enough for both means scrolling
 *      sideways to read a company and losing your place in the list.
 *   2. **Filters live in the URL.** `useSearchParams`, as on the acquisition
 *      log. That is what makes the catalogue's "View companies" an ordinary link
 *      to `/registry?sicCode=78109` rather than cross-screen message passing,
 *      and it makes a filtered registry bookmarkable and shareable.
 *   3. **Page size lives in localStorage.** It is a preference about this
 *      operator's screen, not about this view of the data - it should survive
 *      into the next filter and the next session, which is exactly what the URL
 *      must not do.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CompanyProfile from '../components/CompanyProfile.jsx';
import DeleteSicCodeDialog from '../components/DeleteSicCodeDialog.jsx';
import DirectLookup from '../components/DirectLookup.jsx';
import ExportDialog from '../components/ExportDialog.jsx';
import FavoriteStar from '../components/FavoriteStar.jsx';
import {
  IconAlert,
  IconBuildingSearch,
  IconChevronLeft,
  IconClose,
  IconDownload,
  IconFilter,
  IconRefresh,
  IconSearch,
  IconTrash,
} from '../components/Icons.jsx';
import { companiesApi } from '../lib/api.js';

const PAGE_SIZE_KEY = 'leads-scanner.registry.pageSize';
const FILTERS_KEY = 'leads-scanner.registry.filters';
const PAGE_SIZES = [25, 50, 100, 200];
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Every filter param worth remembering between sessions - everything the
 * toolbar's "Clear filters" button clears, in other words. `offset` and
 * `company` are deliberately excluded: a page position or an open profile
 * from last time is not a "setting", and restoring either on next login
 * would land the operator somewhere arbitrary instead of at a clean,
 * filtered list.
 */
const PERSISTED_FILTER_KEYS = [
  'search',
  'sicCode',
  'status',
  'entityType',
  'hasWebsite',
  'country',
  'location',
  'hasDirector',
  'directorSelectionAmbiguous',
  'acquisitionId',
  'sort',
  'sizeBand',
  'tradingVerdict',
  'tradingAgeBand',
  'trend',
  'profitable',
  'hasCharges',
  'hasInsolvency',
  'filingsOverdue',
  'accountsStale',
  'netAssetsMin',
  'netAssetsMax',
  'turnoverMin',
  'turnoverMax',
];

/** Every filter the drawer's Reset clears (search, entity type and sort are not in the drawer). */
const DRAWER_FILTER_KEYS = PERSISTED_FILTER_KEYS.filter(
  (key) => !['search', 'entityType', 'sort'].includes(key),
);

/**
 * Net assets / turnover filter inputs store plain digits in the URL (so
 * netAssetsMin=1500000 parses cleanly on the API side), but a bare
 * type="number" input can't show thousands separators while typing. These
 * two helpers keep the stored value comma-free while the input displays
 * "1,500,000" - format for display, strip back to digits on change.
 */
function formatThousands(value) {
  if (!value) return '';
  const negative = value.startsWith('-');
  const digits = value.replace(/[^0-9]/g, '');
  if (!digits) return negative ? '-' : '';
  return (negative ? '-' : '') + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function stripThousands(value) {
  const negative = value.startsWith('-');
  const digits = value.replace(/[^0-9]/g, '');
  return (negative ? '-' : '') + digits;
}

/**
 * Small, fixed vocabularies - unlike SIC codes or statuses, these do not need
 * a server round trip to enumerate, so they are declared here rather than
 * added to `/companies/filter-options`.
 */
const SIZE_BAND_OPTIONS = [
  { value: 'micro-entity', label: 'Micro-entity' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'full', label: 'Full accounts' },
  { value: 'group', label: 'Group accounts' },
  { value: 'dormant', label: 'Dormant' },
  { value: 'total-exemption-full', label: 'Total exemption (full)' },
];

const TRADING_VERDICT_OPTIONS = [
  { value: 'actively_trading', label: 'Actively trading' },
  { value: 'minimal_activity', label: 'Minimal activity' },
  { value: 'not_trading', label: 'Not trading' },
  { value: 'insufficient_data', label: 'Insufficient data' },
];

const TRADING_AGE_OPTIONS = [
  { value: 'under1', label: 'Under 1 year' },
  { value: '1-3', label: '1–3 years' },
  { value: '3-5', label: '3–5 years' },
  { value: '5-10', label: '5–10 years' },
  { value: '10+', label: '10+ years' },
];

const TREND_OPTIONS = [
  { value: 'growing', label: 'Growing' },
  { value: 'stable', label: 'Stable' },
  { value: 'declining', label: 'Declining' },
  { value: 'insufficient_data', label: 'Insufficient data' },
];

const PROFITABLE_OPTIONS = [
  { value: 'yes', label: 'Filed a profit' },
  { value: 'no', label: 'Filed a loss' },
  { value: 'not_filed', label: 'No P&L filed' },
];

function readPageSize() {
  try {
    const stored = Number(window.localStorage.getItem(PAGE_SIZE_KEY));
    // Validated against the offered sizes rather than trusted: localStorage is
    // editable, and the API rejects anything over 200 with a 422 the operator
    // would have no way to explain.
    return PAGE_SIZES.includes(stored) ? stored : 50;
  } catch {
    return 50;
  }
}

function formatNumber(value) {
  return typeof value === 'number' ? value.toLocaleString() : '—';
}

/**
 * What each entity-type pill means. `unclassified` gets the longest explanation
 * because it is the one an operator will otherwise read as "not an agency".
 */
const ENTITY_TIPS = {
  agency: 'Classified as a recruitment or employment agency, from its filed SIC codes or its name.',
  employer:
    'Checked, and classified as not an agency. A different answer from "unclassified", which means nothing has checked.',
  unclassified:
    'Nothing has classified these companies yet — usually because they file no SIC codes and their name carries no recognisable keyword. "Unclassified" is not "not an agency"; it means there is no basis for a verdict. Reclassify from the catalogue costs no API calls.',
};

export default function RegistryPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [options, setOptions] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLookup, setShowLookup] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [pageSize, setPageSize] = useState(readPageSize);
  const [reclassifying, setReclassifying] = useState(false);
  const [reclassifyMessage, setReclassifyMessage] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(false);
  const [deleteCategoryMessage, setDeleteCategoryMessage] = useState(null);
  // Bumped after a reclassify so the list and the filter-option counts refetch
  // even though none of the actual filters changed.
  const [refreshToken, setRefreshToken] = useState(0);

  // The filters drawer. Closed by default so the list gets the screen.
  const [drawerOpen, setDrawerOpen] = useState(false);

  const listRef = useRef(null);
  const searchRef = useRef(null);
  const sicFieldRef = useRef(null);
  const sicInputRef = useRef(null);

  // The SIC code filter is an editable combobox, not a plain <select>: typing
  // narrows its list by code or description, and - because filter-options
  // only ever returns the top 60 codes by count - a code that has not
  // accumulated much of the registry yet can still be entered directly.
  const [sicOpen, setSicOpen] = useState(false);
  const [sicDraft, setSicDraft] = useState('');
  const [sicHighlight, setSicHighlight] = useState(-1);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !sicOpen) setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen, sicOpen]);

  // "/" focuses the search box from anywhere on the page, the same shortcut
  // GitHub and similar list-heavy tools use - skipped while a form field
  // already has focus, so it does not steal the character out of what is
  // being typed there.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      const isEditable =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isEditable) return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const search = searchParams.get('search') ?? '';
  const sicCode = searchParams.get('sicCode') ?? '';
  const status = searchParams.get('status') ?? '';
  const entityType = searchParams.get('entityType') ?? '';
  const hasWebsite = searchParams.get('hasWebsite') ?? '';
  const country = searchParams.get('country') ?? '';
  const locationFilter = searchParams.get('location') ?? '';
  const hasDirector = searchParams.get('hasDirector') ?? '';
  const directorSelectionAmbiguous = searchParams.get('directorSelectionAmbiguous') ?? '';
  const acquisitionId = searchParams.get('acquisitionId') ?? '';
  const sort = searchParams.get('sort') ?? '';
  const offset = Number(searchParams.get('offset') ?? 0);
  const selected = searchParams.get('company') ?? '';

  // Phase 7B financial filters.
  const sizeBand = searchParams.get('sizeBand') ?? '';
  const tradingVerdict = searchParams.get('tradingVerdict') ?? '';
  const tradingAgeBand = searchParams.get('tradingAgeBand') ?? '';
  const trend = searchParams.get('trend') ?? '';
  const profitable = searchParams.get('profitable') ?? '';
  const hasCharges = searchParams.get('hasCharges') ?? '';
  const hasInsolvency = searchParams.get('hasInsolvency') ?? '';
  const filingsOverdue = searchParams.get('filingsOverdue') ?? '';
  const accountsStale = searchParams.get('accountsStale') ?? '';
  const netAssetsMin = searchParams.get('netAssetsMin') ?? '';
  const netAssetsMax = searchParams.get('netAssetsMax') ?? '';
  const turnoverMin = searchParams.get('turnoverMin') ?? '';
  const turnoverMax = searchParams.get('turnoverMax') ?? '';

  // The search box is local state so typing stays responsive; the URL catches up
  // after the debounce and the fetch follows the URL.
  const [searchDraft, setSearchDraft] = useState(search);
  // The location box follows the same pattern as the search box - debounced
  // typing, URL as the source of truth once it settles.
  const [locationDraft, setLocationDraft] = useState(locationFilter);

  const setParam = useCallback(
    (key, value) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value) next.set(key, value);
          else next.delete(key);
          // Any change to what is being looked at goes back to page one.
          // Staying on page 5 of a result set that now has two renders an empty
          // list, which reads as a bug rather than as a filter.
          if (key !== 'offset' && key !== 'company') next.delete('offset');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchDraft !== search) setParam('search', searchDraft.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchDraft, search, setParam]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (locationDraft !== locationFilter) setParam('location', locationDraft.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [locationDraft, locationFilter, setParam]);

  // Restores the filters an operator left set the last time they were on this
  // screen, so logging back in shows the same registry rather than a blank
  // one they have to rebuild by hand. Runs once, and only when the URL
  // arrived with none of these params already on it - a link from elsewhere
  // in the app (the catalogue's "View companies", a bookmarked search) is an
  // explicit choice of filters and must win over whatever was remembered.
  // `filtersHydrated` gates the save effect below so it does not fire on this
  // same initial render and overwrite the just-read storage with the blank
  // params the URL still has at that point.
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  useEffect(() => {
    const hasFilterInUrl = PERSISTED_FILTER_KEYS.some((key) => searchParams.has(key));
    if (!hasFilterInUrl) {
      try {
        const stored = window.localStorage.getItem(FILTERS_KEY);
        if (stored) {
          const saved = new URLSearchParams(stored);
          setSearchParams(
            (current) => {
              const next = new URLSearchParams(current);
              for (const key of PERSISTED_FILTER_KEYS) {
                const value = saved.get(key);
                if (value) next.set(key, value);
              }
              return next;
            },
            { replace: true },
          );
          setSearchDraft(saved.get('search') ?? '');
          setLocationDraft(saved.get('location') ?? '');
        }
      } catch {
        // Private browsing, or storage full - the registry just opens unfiltered.
      }
    }
    setFiltersHydrated(true);
    // Deliberately runs once on mount only - re-running on every searchParams
    // change would re-apply the stored filters over whatever the operator is
    // actively changing them to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remembers the current filters for next time - everything "Clear filters"
  // clears. Skipped until hydration above has had its turn, and writes
  // nothing (clearing any previous memory instead) once every persisted key
  // is empty, so pressing "Clear filters" also clears what gets restored on
  // next login rather than bringing the old filters straight back.
  useEffect(() => {
    if (!filtersHydrated) return;
    try {
      const toSave = new URLSearchParams();
      for (const key of PERSISTED_FILTER_KEYS) {
        const value = searchParams.get(key);
        if (value) toSave.set(key, value);
      }
      const serialized = toSave.toString();
      if (serialized) window.localStorage.setItem(FILTERS_KEY, serialized);
      else window.localStorage.removeItem(FILTERS_KEY);
    } catch {
      // Private browsing, or storage full - filters still work for this
      // session; only the memory of them for next time is lost.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersHydrated,
    search,
    sicCode,
    status,
    entityType,
    hasWebsite,
    country,
    locationFilter,
    hasDirector,
    directorSelectionAmbiguous,
    acquisitionId,
    sort,
    sizeBand,
    tradingVerdict,
    tradingAgeBand,
    trend,
    profitable,
    hasCharges,
    hasInsolvency,
    filingsOverdue,
    accountsStale,
    netAssetsMin,
    netAssetsMax,
    turnoverMin,
    turnoverMax,
  ]);

  useEffect(() => {
    companiesApi
      .filterOptions()
      .then(setOptions)
      .catch(() => {
        // A failed options fetch costs the pills, not the list. The registry is
        // still usable with the search box alone, so this is not an error state.
        setOptions(null);
      });
    // Refetched after a reclassify too, since it changes the entity-type counts.
  }, [refreshToken]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    companiesApi
      .list(
        {
          limit: pageSize,
          offset,
          search: search || undefined,
          sicCode: sicCode || undefined,
          status: status || undefined,
          entityType: entityType || undefined,
          hasWebsite: hasWebsite || undefined,
          country: country || undefined,
          location: locationFilter || undefined,
          hasDirector: hasDirector || undefined,
          directorSelectionAmbiguous: directorSelectionAmbiguous || undefined,
          acquisitionId: acquisitionId || undefined,
          sort: sort || undefined,
          sizeBand: sizeBand || undefined,
          tradingVerdict: tradingVerdict || undefined,
          tradingAgeBand: tradingAgeBand || undefined,
          trend: trend || undefined,
          profitable: profitable || undefined,
          hasCharges: hasCharges || undefined,
          hasInsolvency: hasInsolvency || undefined,
          filingsOverdue: filingsOverdue || undefined,
          accountsStale: accountsStale || undefined,
          netAssetsMin: netAssetsMin || undefined,
          netAssetsMax: netAssetsMax || undefined,
          turnoverMin: turnoverMin || undefined,
          turnoverMax: turnoverMax || undefined,
        },
        { signal: controller.signal },
      )
      .then((payload) => {
        setResult(payload);
        setError(null);
        // Back to the top of the list on a new page or a new filter - keeping
        // the scroll position from the previous result set puts the operator
        // somewhere arbitrary in the new one.
        listRef.current?.scrollTo({ top: 0 });
      })
      .catch((caught) => {
        if (caught.name === 'AbortError') return;
        setError(caught.message ?? 'Could not load the registry.');
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [
    pageSize,
    offset,
    search,
    sicCode,
    status,
    entityType,
    hasWebsite,
    country,
    locationFilter,
    hasDirector,
    directorSelectionAmbiguous,
    acquisitionId,
    sort,
    sizeBand,
    tradingVerdict,
    tradingAgeBand,
    trend,
    profitable,
    hasCharges,
    hasInsolvency,
    filingsOverdue,
    accountsStale,
    netAssetsMin,
    netAssetsMax,
    turnoverMin,
    turnoverMax,
    refreshToken,
  ]);

  const choosePageSize = (value) => {
    setPageSize(value);
    try {
      window.localStorage.setItem(PAGE_SIZE_KEY, String(value));
    } catch {
      // Private browsing, or storage full. The size still applies to this
      // session; only the memory of it is lost.
    }
    setParam('offset', '');
  };

  const activeFilters = useMemo(
    () =>
      [
        sicCode,
        status,
        entityType,
        hasWebsite,
        country,
        locationFilter,
        hasDirector,
        directorSelectionAmbiguous,
        acquisitionId,
        search,
        sizeBand,
        tradingVerdict,
        tradingAgeBand,
        trend,
        profitable,
        hasCharges,
        hasInsolvency,
        filingsOverdue,
        accountsStale,
        netAssetsMin,
        netAssetsMax,
        turnoverMin,
        turnoverMax,
      ].filter(Boolean).length,
    [
      sicCode,
      status,
      entityType,
      hasWebsite,
      country,
      locationFilter,
      hasDirector,
      directorSelectionAmbiguous,
      acquisitionId,
      search,
      sizeBand,
      tradingVerdict,
      tradingAgeBand,
      trend,
      profitable,
      hasCharges,
      hasInsolvency,
      filingsOverdue,
      accountsStale,
      netAssetsMin,
      netAssetsMax,
      turnoverMin,
      turnoverMax,
    ],
  );

  /** The same filters the list is reading, shaped for `companiesApi.exportUrl`. */
  const exportFilters = {
    search: search || undefined,
    sicCode: sicCode || undefined,
    status: status || undefined,
    entityType: entityType || undefined,
    hasWebsite: hasWebsite || undefined,
    country: country || undefined,
    location: locationFilter || undefined,
    hasDirector: hasDirector || undefined,
    directorSelectionAmbiguous: directorSelectionAmbiguous || undefined,
    acquisitionId: acquisitionId || undefined,
    sizeBand: sizeBand || undefined,
    tradingVerdict: tradingVerdict || undefined,
    tradingAgeBand: tradingAgeBand || undefined,
    trend: trend || undefined,
    profitable: profitable || undefined,
    hasCharges: hasCharges || undefined,
    hasInsolvency: hasInsolvency || undefined,
    filingsOverdue: filingsOverdue || undefined,
    accountsStale: accountsStale || undefined,
    netAssetsMin: netAssetsMin || undefined,
    netAssetsMax: netAssetsMax || undefined,
    turnoverMin: turnoverMin || undefined,
    turnoverMax: turnoverMax || undefined,
  };

  /**
   * Re-runs agency classification over stored data - no Companies House calls.
   * Scoped to the current SIC-code filter when one is set, otherwise the whole
   * registry, so an operator looking at one code can reclassify just that one
   * without waiting on ~209,000 rows.
   */
  const handleReclassify = async () => {
    setReclassifying(true);
    setReclassifyMessage(null);
    try {
      const result = await companiesApi.reclassify(sicCode || undefined);
      setReclassifyMessage(
        `Reclassified ${result.scanned.toLocaleString()} compan${result.scanned === 1 ? 'y' : 'ies'}: ` +
          `${result.agencies.toLocaleString()} agency, ${result.employers.toLocaleString()} employer, ` +
          `${result.unclassified.toLocaleString()} unclassified.`,
      );
      setRefreshToken((token) => token + 1);
    } catch (caught) {
      setReclassifyMessage(caught.message ?? 'Could not reclassify.');
    } finally {
      setReclassifying(false);
    }
  };

  const clearFilters = () => {
    setSearchDraft('');
    setLocationDraft('');
    setSearchParams(
      (current) => {
        const next = new URLSearchParams();
        // The open company survives a filter reset: clearing the list is not a
        // reason to close what is being read on the right.
        const company = current.get('company');
        if (company) next.set('company', company);
        return next;
      },
      { replace: true },
    );
  };

  const companies = result?.companies ?? [];
  const total = result?.total ?? 0;
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + pageSize, total);

  /**
   * Arrow keys move the selection, which is what makes this list scannable at
   * speed - the whole reason the profile is a pane rather than a page.
   */
  const moveSelection = (direction) => {
    if (companies.length === 0) return;
    const index = companies.findIndex((company) => company.companyNumber === selected);
    const next = Math.min(Math.max(index + direction, 0), companies.length - 1);
    setParam('company', companies[next === -1 ? 0 : next].companyNumber);
  };

  // --- SIC code combobox --------------------------------------------------
  const sicOptions = options?.sicCodes ?? [];
  const selectedSicEntry = sicOptions.find((entry) => entry.code === sicCode) ?? null;

  // While closed, the field shows the committed filter's own code and
  // description; opening it always starts a fresh, empty search rather than
  // editing that label in place, so the full list is one click away.
  const sicDisplayValue = sicOpen
    ? sicDraft
    : sicCode
      ? `${sicCode}${selectedSicEntry ? ` — ${selectedSicEntry.description ?? 'not on the current list'}` : ''}`
      : '';

  const sicQuery = sicDraft.trim().toLowerCase();
  const filteredSicOptions = sicQuery
    ? sicOptions.filter(
        (entry) =>
          entry.code.startsWith(sicQuery) ||
          (entry.description ?? '').toLowerCase().includes(sicQuery),
      )
    : sicOptions;
  // A typed code Companies House would recognise but this list does not offer -
  // the registry's SIC filter is an exact match against company_sic_codes, not
  // a lookup into this list, so it can be applied even though it is not here.
  const sicRawEntryVisible =
    /^\d+$/.test(sicQuery) && !filteredSicOptions.some((entry) => entry.code === sicQuery);
  const sicActiveDescendantId =
    sicHighlight === 0
      ? 'sic-code-option-every'
      : sicHighlight > 0 && sicHighlight <= filteredSicOptions.length
        ? `sic-code-option-${filteredSicOptions[sicHighlight - 1].code}`
        : sicRawEntryVisible && sicHighlight === filteredSicOptions.length + 1
          ? 'sic-code-option-raw'
          : undefined;

  // Closes the list on a click anywhere outside the field. A blur handler
  // alone is not enough - clicking a non-focusable element does not blur the
  // input in every browser - and each option's onMouseDown already prevents
  // this from racing an actual selection.
  useEffect(() => {
    if (!sicOpen) return undefined;
    const handleClickOutside = (event) => {
      if (sicFieldRef.current && !sicFieldRef.current.contains(event.target)) {
        setSicOpen(false);
        setSicHighlight(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sicOpen]);

  const commitSicCode = (code) => {
    setParam('sicCode', code);
    setSicOpen(false);
    setSicHighlight(-1);
    sicInputRef.current?.blur();
  };

  const handleSicFocus = () => {
    setSicOpen(true);
    setSicDraft('');
    setSicHighlight(-1);
  };

  const handleSicChange = (event) => {
    setSicDraft(event.target.value);
    setSicOpen(true);
    setSicHighlight(-1);
  };

  const handleSicKeyDown = (event) => {
    const maxHighlight = filteredSicOptions.length + (sicRawEntryVisible ? 1 : 0);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSicOpen(true);
      setSicHighlight((index) => Math.min(index + 1, maxHighlight));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSicHighlight((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (sicHighlight === 0) {
        commitSicCode('');
      } else if (sicHighlight > 0 && sicHighlight <= filteredSicOptions.length) {
        commitSicCode(filteredSicOptions[sicHighlight - 1].code);
      } else if (sicRawEntryVisible) {
        commitSicCode(sicQuery);
      } else if (filteredSicOptions.length === 1) {
        commitSicCode(filteredSicOptions[0].code);
      }
    } else if (event.key === 'Escape') {
      setSicOpen(false);
      setSicHighlight(-1);
      event.currentTarget.blur();
    }
  };

  // --- Filters drawer -----------------------------------------------------
  // Flag filters share one shape so the drawer's checkboxes and the chips
  // above the list are driven by the same list.
  const flagFilters = [
    {
      key: 'hasWebsite',
      value: hasWebsite,
      label: 'Has website',
      count: options?.website?.withWebsite,
      tip: 'Companies House publishes no website field - this is derived: domain guessing confirmed against the page, DuckDuckGo as the fallback. Only companies acquired at Full depth, or refreshed individually, have been checked.',
    },
    {
      key: 'hasDirector',
      value: hasDirector,
      label: 'Has director',
      count: options?.directors?.withDirector,
      tip: 'Resolved from persons with significant control first, the officers list as a fallback. A company acquired below Full depth has none yet.',
    },
    {
      key: 'directorSelectionAmbiguous',
      value: directorSelectionAmbiguous,
      label: 'Needs director review',
      count: options?.directors?.ambiguous,
      tip: 'Director resolution found a genuine tie - a company owned equally by two people, or several equally active officers - and needs a human to pick the primary.',
    },
    {
      key: 'filingsOverdue',
      value: filingsOverdue,
      label: 'Filings overdue',
      tip: 'Accounts or confirmation statement overdue - a well-established early distress signal.',
    },
    {
      key: 'hasInsolvency',
      value: hasInsolvency,
      label: 'Insolvency history',
      tip: 'Companies House has recorded at least one insolvency case against this company.',
    },
    {
      key: 'hasCharges',
      value: hasCharges,
      label: 'Outstanding charges',
      tip: 'At least one outstanding charge (mortgage, debenture) registered against this company.',
    },
    {
      key: 'accountsStale',
      value: accountsStale,
      label: 'Accounts stale',
      tip: 'Filed accounts are more than 21 months old - accounts can legitimately be up to 21 months old and still be the newest filed, so this flags genuinely stale ones.',
    },
  ];

  const labelOf = (list, value) => list.find((entry) => entry.value === value)?.label ?? value;
  const clearParams = (...keys) => () => {
    if (keys.includes('location')) setLocationDraft('');
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        keys.forEach((key) => next.delete(key));
        next.delete('offset');
        return next;
      },
      { replace: true },
    );
  };
  const rangeLabel = (name, min, max) =>
    `${name} £${min ? formatThousands(min) : 'any'} – ${max ? formatThousands(max) : 'any'}`;

  // One chip per drawer filter that is on. Search and the entity-type pills
  // are not here - they have their own controls on the page.
  const filterChips = [
    sicCode && {
      key: 'sicCode',
      label: `SIC ${sicCode}${selectedSicEntry?.description ? ` · ${selectedSicEntry.description}` : ''}`,
      clear: clearParams('sicCode'),
    },
    status && {
      key: 'status',
      label: labelOf(options?.statuses ?? [], status),
      clear: clearParams('status'),
    },
    locationFilter && {
      key: 'location',
      label: `Location: ${locationFilter}`,
      clear: clearParams('location'),
    },
    country && { key: 'country', label: `Country: ${country}`, clear: clearParams('country') },
    acquisitionId && {
      key: 'acquisitionId',
      label: 'From one acquisition run',
      clear: clearParams('acquisitionId'),
    },
    trend && {
      key: 'trend',
      label: `Net assets ${labelOf(TREND_OPTIONS, trend).toLowerCase()}`,
      clear: clearParams('trend'),
    },
    tradingAgeBand && {
      key: 'tradingAgeBand',
      label: `Trading ${labelOf(TRADING_AGE_OPTIONS, tradingAgeBand)}`,
      clear: clearParams('tradingAgeBand'),
    },
    profitable && {
      key: 'profitable',
      label: labelOf(PROFITABLE_OPTIONS, profitable),
      clear: clearParams('profitable'),
    },
    sizeBand && {
      key: 'sizeBand',
      label: labelOf(SIZE_BAND_OPTIONS, sizeBand),
      clear: clearParams('sizeBand'),
    },
    tradingVerdict && {
      key: 'tradingVerdict',
      label: labelOf(TRADING_VERDICT_OPTIONS, tradingVerdict),
      clear: clearParams('tradingVerdict'),
    },
    (netAssetsMin || netAssetsMax) && {
      key: 'netAssets',
      label: rangeLabel('Net assets', netAssetsMin, netAssetsMax),
      clear: clearParams('netAssetsMin', 'netAssetsMax'),
    },
    (turnoverMin || turnoverMax) && {
      key: 'turnover',
      label: rangeLabel('Turnover', turnoverMin, turnoverMax),
      clear: clearParams('turnoverMin', 'turnoverMax'),
    },
    ...flagFilters
      .filter((flag) => flag.value === 'true')
      .map((flag) => ({ key: flag.key, label: flag.label, clear: clearParams(flag.key) })),
  ].filter(Boolean);

  /** Empties everything in the drawer; search, entity type and sort stay. */
  const resetDrawerFilters = () => {
    setLocationDraft('');
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        DRAWER_FILTER_KEYS.forEach((key) => next.delete(key));
        next.delete('offset');
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="registry-page">
      <div className="registry-toolbar">
        <div className="row row-between">
          <span className="card-title">Company registry</span>
          <span className="row row-tight">
            <button
              type="button"
              className="btn btn-sm tip tip-below tip-left"
              onClick={() => void handleReclassify()}
              disabled={reclassifying}
              data-tip={
                sicCode
                  ? `Re-run agency classification for companies filing ${sicCode}, from their name and SIC codes already held. No Companies House calls.`
                  : 'Re-run agency classification for every company in the registry, from names and SIC codes already held. No Companies House calls.'
              }
            >
              {reclassifying ? <span className="spinner" /> : <IconRefresh />}
              Reclassify{sicCode ? ` ${sicCode}` : ' all'}
            </button>
            <button
              type="button"
              className="btn btn-sm tip tip-below tip-left"
              onClick={() => setShowLookup((open) => !open)}
              data-tip="Fetch one company straight from Companies House by name or number, without acquiring its whole SIC code. Spends one API call."
            >
              <IconBuildingSearch />
              Look up
            </button>
            <button
              type="button"
              className="btn btn-sm tip tip-below tip-left"
              onClick={() => setShowExport(true)}
              data-tip="Choose a file format and which columns to include, then download every company matching these filters. Streamed, so a large export does not need to fit in memory first."
            >
              <IconDownload />
              Export{total ? ` (${total.toLocaleString()})` : ''}
            </button>
          </span>
        </div>

        {reclassifyMessage ? <p className="text-xs text-dim">{reclassifyMessage}</p> : null}
        {deleteCategoryMessage ? <p className="text-xs text-dim">{deleteCategoryMessage}</p> : null}

        {showLookup ? (
          <DirectLookup
            onClose={() => setShowLookup(false)}
            // Selecting it is the whole response, and deliberately so. An
            // imported company usually falls outside whatever filters the
            // list is under, so refreshing the list would leave the operator
            // looking for a row that is not there. Opening it on the right
            // answers the question they actually asked.
            onImported={(companyNumber) => setParam('company', companyNumber)}
          />
        ) : null}

        {/* --- Search bar ------------------------------------------------
            Search, the Filters drawer trigger and Sort share one bar. Every
            other filter lives in the drawer below; each one writes straight
            to the URL, so the list behind the drawer re-fetches as the
            operator changes them. */}
        <div className="registry-command">
          <div className="search-field registry-command-search">
            <span className="search-field-icon">
              <IconSearch />
            </span>
            <input
              ref={searchRef}
              className="input"
              type="search"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  moveSelection(1);
                }
                if (event.key === 'Escape') {
                  event.currentTarget.blur();
                }
              }}
              placeholder="Search by company name or number"
              aria-label="Search the registry by company name or number. Press slash from anywhere on this page to focus it."
              title="Press / from anywhere on this page to jump here"
            />
          </div>

          <button
            type="button"
            className="btn registry-filter-btn"
            onClick={() => setDrawerOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={drawerOpen}
          >
            <IconFilter />
            Filters
            {filterChips.length > 0 ? (
              <span className="registry-filter-badge">{filterChips.length}</span>
            ) : null}
          </button>

          <label className="registry-sort">
            <span className="sr-only">Sort by</span>
            <select
              className="select"
              value={result?.sort ?? sort}
              onChange={(event) => setParam('sort', event.target.value)}
              aria-label="Sort the registry"
            >
              {(options?.sorts ?? []).map((entry) => (
                <option
                  key={entry.value}
                  value={entry.value}
                  // "Best match" needs a MATCH in the query to score against.
                  disabled={entry.requiresSearch && !search}
                >
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {result?.searchNote ? (
          <p className="text-xs text-faint">{result.searchNote}</p>
        ) : null}

        {/* Every drawer filter that is on, as a removable chip. */}
        {filterChips.length > 0 ? (
          <div className="filter-chips" aria-live="polite">
            <span className="filter-chips-label">Looking for</span>
            {filterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                className="filter-chip"
                onClick={chip.clear}
                aria-label={`Remove filter: ${chip.label}`}
              >
                <span className="filter-chip-text">{chip.label}</span>
                <IconClose />
              </button>
            ))}
            <button type="button" className="filter-chips-clear" onClick={clearFilters}>
              Clear all
            </button>
          </div>
        ) : null}

        <div className="pill-row">
          <button
            type="button"
            className={`pill${entityType === '' ? ' is-active' : ''}`}
            onClick={() => setParam('entityType', '')}
          >
            All
          </button>
          {(options?.entityTypes ?? []).map((entry) => (
            <button
              key={entry.value}
              type="button"
              className={`pill${entityType === entry.value ? ' is-active' : ''} tip tip-below`}
              onClick={() => setParam('entityType', entry.value)}
              data-tip={ENTITY_TIPS[entry.value]}
            >
              {entry.label}
              <span className="pill-count">{entry.count.toLocaleString()}</span>
            </button>
          ))}

          {activeFilters > 0 && result?.summary ? (
            <span className="text-xs text-dim">{result.summary}</span>
          ) : null}

          {filterChips.length === 0 && activeFilters > 0 ? (
            <button type="button" className="btn btn-ghost btn-sm spacer" onClick={clearFilters}>
              Clear {activeFilters} {activeFilters === 1 ? 'filter' : 'filters'}
            </button>
          ) : null}
        </div>

        {/* --- Filters drawer --------------------------------------------
            The scrim is deliberately light so the list stays visible - and
            visibly re-filters - behind the drawer. */}
        {drawerOpen ? (
          <>
            <div className="filter-drawer-scrim" onClick={() => setDrawerOpen(false)} />
            <aside className="filter-drawer" role="dialog" aria-label="Registry filters">
              <header className="filter-drawer-header">
                <div>
                  <p className="filter-drawer-kicker">Refine this view</p>
                  <h2 className="filter-drawer-title">Filters</h2>
                </div>
                <button
                  type="button"
                  className="btn btn-icon"
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close filters"
                >
                  <IconClose />
                </button>
              </header>

              <div className="filter-drawer-body">
                <section className="filter-section">
                  <h3 className="filter-section-title">Company</h3>

              <label className="filter-control" ref={sicFieldRef}>
                <span className="filter-label">
                  <IconFilter />
                  SIC code
                </span>
                <span className="row row-tight">
                  <span className="combobox">
                    <input
                      ref={sicInputRef}
                      className="input combobox-input"
                      type="text"
                      role="combobox"
                      aria-expanded={sicOpen}
                      aria-haspopup="listbox"
                      aria-controls="sic-code-listbox"
                      aria-autocomplete="list"
                      aria-activedescendant={sicActiveDescendantId}
                      value={sicDisplayValue}
                      onFocus={handleSicFocus}
                      onChange={handleSicChange}
                      onKeyDown={handleSicKeyDown}
                      onBlur={() => setSicOpen(false)}
                      placeholder={sicOpen ? 'Type a code or keyword…' : 'Every code'}
                    />
                    {!sicOpen && sicCode ? (
                      <button
                        type="button"
                        className="search-field-clear btn btn-ghost btn-icon btn-sm"
                        onClick={() => commitSicCode('')}
                        aria-label="Clear the SIC code filter"
                      >
                        <IconClose />
                      </button>
                    ) : null}
                    {sicOpen ? (
                      <ul className="combobox-list" role="listbox" id="sic-code-listbox">
                        <li
                          id="sic-code-option-every"
                          role="option"
                          aria-selected={sicCode === ''}
                          className={`combobox-option${sicHighlight === 0 ? ' is-active' : ''}`}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            commitSicCode('');
                          }}
                          onMouseEnter={() => setSicHighlight(0)}
                        >
                          Every code
                        </li>
                        {filteredSicOptions.map((entry, index) => (
                          <li
                            key={entry.code}
                            id={`sic-code-option-${entry.code}`}
                            role="option"
                            aria-selected={entry.code === sicCode}
                            className={`combobox-option${sicHighlight === index + 1 ? ' is-active' : ''}`}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              commitSicCode(entry.code);
                            }}
                            onMouseEnter={() => setSicHighlight(index + 1)}
                          >
                            <span className="combobox-option-code">{entry.code}</span>
                            <span className="combobox-option-desc">
                              {entry.description ?? 'not on the current list'}
                            </span>
                            <span className="combobox-option-count">
                              {entry.count.toLocaleString()}
                            </span>
                          </li>
                        ))}
                        {filteredSicOptions.length === 0 && !sicRawEntryVisible ? (
                          <li className="combobox-empty">
                            No listed code matches &ldquo;{sicDraft.trim()}&rdquo;.
                          </li>
                        ) : null}
                        {sicRawEntryVisible ? (
                          <li
                            id="sic-code-option-raw"
                            role="option"
                            className={`combobox-option combobox-option-raw${
                              sicHighlight === filteredSicOptions.length + 1 ? ' is-active' : ''
                            }`}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              commitSicCode(sicQuery);
                            }}
                            onMouseEnter={() => setSicHighlight(filteredSicOptions.length + 1)}
                          >
                            Filter by &ldquo;{sicQuery}&rdquo; directly — not one of the top 60
                            codes listed here
                          </li>
                        ) : null}
                      </ul>
                    ) : null}
                  </span>
                  {sicCode ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-icon btn-sm tip tip-below tip-left"
                      onClick={() => setDeletingCategory(true)}
                      data-tip={`Permanently delete every company filed under ${sicCode} from the registry.`}
                      aria-label={`Delete every company filed under ${sicCode}`}
                    >
                      <IconTrash />
                    </button>
                  ) : null}
                </span>
              </label>

                  <label className="filter-control">
                    <span className="filter-label">Status</span>
                    <select
                      className="select"
                      value={status}
                      onChange={(event) => setParam('status', event.target.value)}
                    >
                      <option value="">Every status</option>
                      {(options?.statuses ?? []).map((entry) => (
                        <option key={entry.value} value={entry.value}>
                          {entry.label} ({entry.count.toLocaleString()})
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="filter-control">
                    <span className="filter-label">Location</span>
                    <div className="search-field">
                      <span className="search-field-icon">
                        <IconSearch />
                      </span>
                      <input
                        className="input"
                        type="search"
                        value={locationDraft}
                        onChange={(event) => setLocationDraft(event.target.value)}
                        placeholder="Address, town or postcode"
                        aria-label="Filter by the registered address - address line, town, region or postcode"
                      />
                    </div>
                  </div>

                  <label className="filter-control">
                    <span className="filter-label">Country</span>
                    <select
                      className="select"
                      value={country}
                      onChange={(event) => setParam('country', event.target.value)}
                    >
                      <option value="">Every country</option>
                      {(options?.countries ?? []).map((entry) => (
                        <option key={entry.value} value={entry.value}>
                          {entry.value} ({entry.count.toLocaleString()})
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="filter-control">
                    <span className="filter-label">From run</span>
                    <select
                      className="select"
                      value={acquisitionId}
                      onChange={(event) => setParam('acquisitionId', event.target.value)}
                    >
                      <option value="">Any acquisition</option>
                      {(options?.acquisitions ?? []).map((run) => (
                        <option key={run.uuid} value={run.uuid}>
                          {run.sicCode} · {new Date(run.createdAt).toLocaleDateString()} (
                          {run.companiesFound.toLocaleString()})
                        </option>
                      ))}
                    </select>
                  </label>
                </section>

                <section className="filter-section">
                  <h3 className="filter-section-title">Financial profile</h3>

                  <label className="filter-control">
                    <span className="filter-label">Net assets trend</span>
                    <select
                      className="select"
                      value={trend}
                      onChange={(event) => setParam('trend', event.target.value)}
                    >
                      <option value="">Any trend</option>
                      {TREND_OPTIONS.map((entry) => (
                        <option key={entry.value} value={entry.value}>
                          {entry.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="filter-control">
                    <span className="filter-label">Trading age</span>
                    <select
                      className="select"
                      value={tradingAgeBand}
                      onChange={(event) => setParam('tradingAgeBand', event.target.value)}
                    >
                      <option value="">Any age</option>
                      {TRADING_AGE_OPTIONS.map((entry) => (
                        <option key={entry.value} value={entry.value}>
                          {entry.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label
                    className="filter-control tip tip-below"
                    data-tip="Real filed profit (medium/large accounts only) - never the retained-earnings-movement proxy small companies get instead."
                  >
                    <span className="filter-label">Profit filed</span>
                    <select
                      className="select"
                      value={profitable}
                      onChange={(event) => setParam('profitable', event.target.value)}
                    >
                      <option value="">Any</option>
                      {PROFITABLE_OPTIONS.map((entry) => (
                        <option key={entry.value} value={entry.value}>
                          {entry.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="filter-control">
                    <span className="filter-label">Size band</span>
                    <select
                      className="select"
                      value={sizeBand}
                      onChange={(event) => setParam('sizeBand', event.target.value)}
                    >
                      <option value="">Every size</option>
                      {SIZE_BAND_OPTIONS.map((entry) => (
                        <option key={entry.value} value={entry.value}>
                          {entry.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="filter-control">
                    <span className="filter-label">Trading verdict</span>
                    <select
                      className="select"
                      value={tradingVerdict}
                      onChange={(event) => setParam('tradingVerdict', event.target.value)}
                    >
                      <option value="">Any verdict</option>
                      {TRADING_VERDICT_OPTIONS.map((entry) => (
                        <option key={entry.value} value={entry.value}>
                          {entry.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="filter-control">
                    <span className="filter-label">
                      Net assets £
                      <span
                        className="info-tip tip tip-below"
                        data-tip="A company's own capital and reserves as most recently filed, from Financials-depth acquisition or a per-company Fetch accounts."
                      >
                        i
                      </span>
                    </span>
                    <span className="row row-tight">
                      <input
                        className="input"
                        type="text"
                        inputMode="numeric"
                        placeholder="Min"
                        aria-label="Net assets minimum"
                        value={formatThousands(netAssetsMin)}
                        onChange={(event) => setParam('netAssetsMin', stripThousands(event.target.value))}
                      />
                      <input
                        className="input"
                        type="text"
                        inputMode="numeric"
                        placeholder="Max"
                        aria-label="Net assets maximum"
                        value={formatThousands(netAssetsMax)}
                        onChange={(event) => setParam('netAssetsMax', stripThousands(event.target.value))}
                      />
                    </span>
                  </div>

                  <div className="filter-control">
                    <span className="filter-label">
                      Turnover £
                      <span
                        className="info-tip tip tip-below"
                        data-tip={
                          result?.turnoverCoverage
                            ? `${result.turnoverCoverage.withValue.toLocaleString()} of ${result.turnoverCoverage.total.toLocaleString()} companies in the current result have a turnover figure at all - UK small and micro companies are not required to file one.`
                            : 'UK small and micro companies are not required to file a turnover figure, so most companies here will have none.'
                        }
                      >
                        i
                      </span>
                    </span>
                    <span className="row row-tight">
                      <input
                        className="input"
                        type="text"
                        inputMode="numeric"
                        placeholder="Min"
                        aria-label="Turnover minimum"
                        value={formatThousands(turnoverMin)}
                        onChange={(event) => setParam('turnoverMin', stripThousands(event.target.value))}
                      />
                      <input
                        className="input"
                        type="text"
                        inputMode="numeric"
                        placeholder="Max"
                        aria-label="Turnover maximum"
                        value={formatThousands(turnoverMax)}
                        onChange={(event) => setParam('turnoverMax', stripThousands(event.target.value))}
                      />
                    </span>
                  </div>
                </section>

                <section className="filter-section">
                  <h3 className="filter-section-title">Company signals</h3>
                  {flagFilters.map((flag) => (
                    <label key={flag.key} className="filter-check tip tip-below tip-left" data-tip={flag.tip}>
                      <input
                        type="checkbox"
                        checked={flag.value === 'true'}
                        onChange={() => setParam(flag.key, flag.value === 'true' ? '' : 'true')}
                      />
                      <span className="filter-check-label">{flag.label}</span>
                      {typeof flag.count === 'number' ? (
                        <span className="filter-check-count">{flag.count.toLocaleString()}</span>
                      ) : null}
                    </label>
                  ))}
                </section>
              </div>

              <footer className="filter-drawer-footer">
                <button
                  type="button"
                  className="btn"
                  onClick={resetDrawerFilters}
                  disabled={filterChips.length === 0}
                >
                  Reset
                </button>
                <button type="button" className="btn btn-primary" onClick={() => setDrawerOpen(false)}>
                  {loading && !result
                    ? 'Show companies'
                    : `Show ${formatNumber(total)} ${total === 1 ? 'company' : 'companies'}`}
                </button>
              </footer>
            </aside>
          </>
        ) : null}
      </div>

      <div className="master-detail">
        <div className="master">
          {/* --- The list ------------------------------------------------- */}
          {error ? (
            <div className="master-list">
              <div className="alert alert-error">
                <span className="alert-icon">
                  <IconAlert />
                </span>
                <span className="alert-body">
                  <span className="alert-title">Could not load the registry</span>
                  {error}
                </span>
              </div>
            </div>
          ) : loading && !result ? (
            <div className="master-list">
              <div className="loading-panel">
                <span className="spinner" />
                Loading companies
              </div>
            </div>
          ) : companies.length === 0 ? (
            <div className="master-list">
              <div className="empty">
                <h3 className="empty-title">
                  {activeFilters > 0
                    ? 'No company matches these filters'
                    : 'The registry is empty'}
                </h3>
                <p className="empty-description">
                  {activeFilters > 0
                    ? 'Press Clear filters above to see every company, or use Look up to fetch a specific one from Companies House.'
                    : 'Companies arrive here from an acquisition run. Choose a SIC code in the catalogue and press Acquire, or use Look up above to fetch a single company by name or number.'}
                </p>
                <div className="empty-actions">
                  <Link className="btn btn-primary" to="/catalogue">
                    Go to the catalogue
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className={`master-list${loading ? ' is-loading' : ''}`} ref={listRef}>
              {companies.map((company) => (
                <div
                  key={company.companyNumber}
                  role="button"
                  tabIndex={0}
                  className={`master-item${selected === company.companyNumber ? ' is-selected' : ''}`}
                  onClick={() => setParam('company', company.companyNumber)}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') {
                      event.preventDefault();
                      moveSelection(1);
                    }
                    if (event.key === 'ArrowUp') {
                      event.preventDefault();
                      moveSelection(-1);
                    }
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setParam('company', company.companyNumber);
                    }
                  }}
                >
                  <span className="master-item-title-row">
                    <span className="master-item-title">{company.name}</span>
                    <FavoriteStar companyNumber={company.companyNumber} />
                  </span>
                  <span className="master-item-meta">
                    <span className="mono">{company.companyNumber}</span>
                    <span
                      className={`badge badge-sm ${
                        company.status === 'active' ? 'badge-ok' : 'badge-unknown'
                      }`}
                    >
                      <span className="badge-dot" />
                      {company.statusLabel ?? 'Unknown'}
                    </span>
                    {company.locality ? <span>{company.locality}</span> : null}
                  </span>
                  {/* Codes with descriptions, never bare numbers - the brief's
                      done-criterion for this phase, and the thing that makes the
                      list scannable without opening every row. */}
                  <span className="master-item-codes">
                    {company.sicCodes.slice(0, 2).map((entry) => (
                      <span key={entry.code} className="master-item-code">
                        <span className="mono">{entry.code}</span>{' '}
                        {entry.description ?? 'not on the current list'}
                      </span>
                    ))}
                    {company.sicCodeCount > 2 ? (
                      <span className="text-xs text-faint">
                        +{company.sicCodeCount - 2} more
                      </span>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="master-footer">
            <span>
              {formatNumber(showingFrom)}–{formatNumber(showingTo)} of {formatNumber(total)}
            </span>

            <label className="master-footer-size">
              <span className="sr-only">Companies per page</span>
              <select
                className="select select-sm"
                value={pageSize}
                onChange={(event) => choosePageSize(Number(event.target.value))}
                aria-label="Companies per page"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            </label>

            <span className="spacer" />

            <button
              type="button"
              className="btn btn-sm"
              disabled={offset === 0}
              onClick={() => setParam('offset', String(Math.max(0, offset - pageSize)))}
              aria-label="Previous page"
            >
              <IconChevronLeft />
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={showingTo >= total}
              onClick={() => setParam('offset', String(offset + pageSize))}
            >
              Next
            </button>
          </div>
        </div>

        <CompanyProfile
          companyNumber={selected || null}
          onDeleted={() => {
            // The deleted company can no longer be shown on the right, and the
            // list/filter counts it contributed to are now stale - the same
            // refresh a reclassify triggers, reusing that mechanism rather
            // than duplicating it.
            setParam('company', '');
            setRefreshToken((token) => token + 1);
          }}
        />
      </div>

      {showExport ? (
        <ExportDialog filters={exportFilters} total={total} onClose={() => setShowExport(false)} />
      ) : null}

      {deletingCategory ? (
        <DeleteSicCodeDialog
          sicCode={sicCode}
          description={options?.sicCodes?.find((entry) => entry.code === sicCode)?.description}
          onCancel={() => setDeletingCategory(false)}
          onDeleted={({ companies, runs }) => {
            setDeletingCategory(false);
            setDeleteCategoryMessage(
              `Deleted ${companies.toLocaleString()} compan${companies === 1 ? 'y' : 'ies'} and ` +
                `${runs.toLocaleString()} acquisition run${runs === 1 ? '' : 's'} for ${sicCode}.`,
            );
            // The deleted companies can no longer be shown, and the open
            // profile (if any) may have just been one of them - closing it
            // and bumping refreshToken reuses the same refresh a reclassify
            // or a single-company delete already triggers.
            setParam('company', '');
            setRefreshToken((token) => token + 1);
          }}
        />
      ) : null}
    </div>
  );
}
